<script setup lang="ts" generic="T, C extends ColumnModel, R extends RecordMap<C>">
import { api } from "@/api";
import { PV } from "@/widgets";
import { useDebounceFn } from "@vueuse/core";
import { delay, isNil, isString } from "es-toolkit";
import type { DataTablePageEvent } from "primevue";
import { computed, nextTick, ref, watch } from "vue";
import { useProfileStore } from "../../stores/profile.store";
import type { ColumnModel, RecordMap, TableColumn, defineTable } from "../common";

const props = defineProps<{
    table: ReturnType<typeof defineTable<T, C, R>>;
}>();

const slots = defineSlots<
    TableColumn<R> & {
        _tools: (props: { refresh: () => Promise<void> }) => any;
        _buttons: () => any;
    }
>();

const pageModel = defineModel<number>("page", {
    default: 0,
    required: false,
});

const rowsModel = defineModel<number>("rows", {
    default: 100,
    required: false,
});

const searchModel = defineModel<string | undefined>("search", {
    default: "",
    required: false,
});

const profile = useProfileStore().data;
const visible = computed(() => {
    const kind = profile?.kind;
    if (isNil(kind)) return [];
    return props.table.visible[kind] ?? [];
});

const records = ref<R[]>([]);
const totalRecords = ref(0);
const showLoading = ref(false);
const tableCols = computed(() => {
    const keys = new Set(visible.value);
    return props.table.columns.filter((c) => keys.has(c.field));
});

function castName(name: string) {
    return name as keyof typeof slots;
}

async function refresh() {
    const params = {
        page: pageModel.value,
        rows: rowsModel.value,
        search: searchModel.value,
    };

    showLoading.value = true;
    records.value = [...new Array(params.rows)];
    await nextTick();
    const startMs = Date.now();

    const src = props.table.src;
    let data: { records: T[]; totalRecords: number } | undefined;
    if (isString(src)) {
        data = await api.raw.post<typeof params, typeof data>(src, params).execute();
    } else {
        data = await src(params).execute();
    }

    const doneMs = Date.now();
    if (import.meta.env.DEV && doneMs - startMs < 250) await delay(250 - (doneMs - startMs));
    showLoading.value = false;

    if (data === undefined) return;
    records.value = data.records.map(props.table.mapping);
    totalRecords.value = data.totalRecords;
}

async function onPageChange(event: DataTablePageEvent) {
    pageModel.value = event.page;
    rowsModel.value = event.rows;
}

const refreshFn = useDebounceFn(refresh, 500, {
    maxWait: 2000,
    rejectOnCancel: false,
});

watch(searchModel, refreshFn);
watch([pageModel, rowsModel], refresh, { immediate: true });
</script>

<template>
    <PV.DataTable
        currentPageReportTemplate="Menampilkan {first} - {last} dari {totalRecords} baris"
        lazy
        :loading="showLoading"
        paginator
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
        reorderableColumns
        :rows="rowsModel"
        :rowsPerPageOptions="[5, 10, 25, 50]"
        :scrollHeight="`${60 * records.length}px`"
        scrollable
        stripedRows
        :totalRecords="totalRecords"
        :value="records"
        :virtualScrollerOptions="{ itemSize: 60 }"
        @page="onPageChange"
    >
        <template #header>
            <div class="flex flex-row items-center justify-center py-1">
                <div class="w-3/12 flex flex-row justify-start">
                    <!-- <PV.Button :icon="PrimeIcons.WINDOW_MAXIMIZE" severity="secondary" size="small" text @click="$emit('expand')" /> -->
                    <slot v-if="slots['_tools'] !== undefined" name="_tools" :refresh="refresh" />
                </div>

                <PV.IconField class="grow">
                    <PV.InputIcon class="pi pi-search" />
                    <PV.InputText v-model.trim="searchModel" fluid placeholder="Cari" size="small" />
                </PV.IconField>

                <div class="w-3/12 flex flex-row justify-end">
                    <slot v-if="slots['_buttons'] !== undefined" name="_buttons" />
                </div>
            </div>
        </template>

        <template v-if="showLoading">
            <template v-for="column in tableCols">
                <PV.Column :header="column.title">
                    <template #body>
                        <PV.Skeleton />
                    </template>
                </PV.Column>
            </template>
        </template>
        <template v-else>
            <template v-for="column in tableCols">
                <PV.Column :field="column.field" :header="column.title">
                    <template v-if="slots[castName(column.field)] !== undefined" #body="{ data }">
                        <slot :data="data" :name="castName(column.field)" />
                    </template>
                </PV.Column>
            </template>
        </template>

        <template #empty>
            <div class="flex items-center justify-center h-75">Tidak ada data.</div>
        </template>
    </PV.DataTable>
</template>
